/**
 * Remote search select for use within Filterrific forms.
 *
 * Wraps a <select> element with TomSelect remote search. The parent form is
 * submitted via its own "Apply filters" button, not on change.
 *
 * Values:
 *   url        - The search endpoint URL. Should accept a `query` param and
 *                return JSON array of {id, text} objects.
 *   preloadId  - (optional) ID of a pre-selected item to load on connect.
 *   preloadText - (optional) Display text for the pre-selected item.
 *
 * Example:
 *   %select{ data: { controller: "filterrific-remote-select",
 *     "filterrific-remote-select-url-value": users_search_path } }
 */
import TomSelectController from "../tom_select_controller";

export default class extends TomSelectController {
  static values = {
    url: String,
    preloadId: String,
    preloadText: String,
  };

  configuration(overrides = {}) {
    return super.configuration({
      plugins: ["dropdown_input"],
      labelField: "text",
      valueField: "id",
      searchField: ["text"],
      sortField: [{ field: "$order" }, { field: "$score" }],
      closeAfterSelect: true,
      openOnFocus: true,
      allowEmptyOption: true,
      maxOptions: null,
      load: (q, callback) => this.search(q, callback),
      onFocus: () => this.loadInitialResults(),
      onItemAdd: () => this.tomselect.blur(),
      ...overrides,
    });
  }

  connect() {
    this.initialResultsLoaded = false;
    this.emptyLabel = this.element.querySelector('option[value=""]')?.textContent || "";

    if (!super.connect()) return;

    // Pre-load the selected option if provided
    if (this.preloadIdValue && this.preloadTextValue) {
      this.tomselect.addOption({
        id: this.preloadIdValue,
        text: this.preloadTextValue,
      });
      this.tomselect.setValue(this.preloadIdValue, true);
    }
  }

  loadInitialResults() {
    if (this.initialResultsLoaded) return;
    this.initialResultsLoaded = true;

    fetch(this.urlValue)
      .then((response) => response.json())
      .then((json) => {
        json.forEach((item) => this.tomselect.addOption(item));
        this.tomselect.refreshOptions(this.tomselect.isOpen);
      })
      .catch(() => {});
  }

  search(q, callback) {
    if (!q.length) {
      callback();
      return;
    }

    var url =
      this.urlValue + (this.urlValue.includes("?") ? "&" : "?") + new URLSearchParams({ query: q });

    fetch(url)
      .then((response) => response.json())
      .then((json) => {
        this.tomselect.clearOptions();
        this.tomselect.addOption({ id: "", text: this.emptyLabel });
        callback(json);
      })
      .catch(() => {
        callback();
      });
  }
}
